// TMOS Admin Notification Management Page
// Date: November 15, 2025
// Purpose: Platform admin page for managing admin notifications

import React, { useState } from 'react';
import { 
  BellIcon
} from '@heroicons/react/24/outline';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { FlexibleCRUDTable } from '@/components/common/FlexibleCRUDTable';
import { ViewModal } from '@/components/common/ViewModal';
import { EditModal } from '@/components/common/EditModal';
import { DynamicForm } from '@/components/common/DynamicForm';
import { adminNotificationEntity } from '@/config/entities/adminNotifications';
import { adminNotificationService, AdminNotification, CreateAdminNotificationRequest, UpdateAdminNotificationRequest } from '@/services/adminNotificationService';
import { useLanguage } from '@/state/language/LanguageProvider';
import { toast } from 'react-hot-toast';

export const AdminNotificationManagementPage: React.FC = () => {
  const { t } = useLanguage();
  const [selectedNotification, setSelectedNotification] = useState<AdminNotification | null>(null);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  // Service wrapper for FlexibleCRUDTable
  const notificationService = {
    getAll: async () => {
      return await adminNotificationService.getAllNotifications();
    },
    get: async (id: string) => {
      return await adminNotificationService.getNotificationById(id);
    },
    create: async (data: CreateAdminNotificationRequest) => {
      return await adminNotificationService.createNotification(data);
    },
    update: async (id: string, data: UpdateAdminNotificationRequest) => {
      return await adminNotificationService.updateNotification(id, data);
    },
    delete: async (id: string) => {
      return await adminNotificationService.deleteNotification(id);
    }
  };

  const handleView = (notification: AdminNotification) => {
    setSelectedNotification(notification);
    setIsViewModalOpen(true);
  };

  const handleEdit = (notification: AdminNotification) => {
    setSelectedNotification(notification);
    setIsEditModalOpen(true);
  };

  const handleDelete = async (notification: AdminNotification) => {
    if (window.confirm(t('admin.notifications.confirmDelete') || 'Are you sure you want to delete this notification?')) {
      try {
        await adminNotificationService.deleteNotification(notification.id);
        toast.success(t('admin.notifications.deletedSuccess') || 'Notification deleted successfully');
      } catch (error: any) {
        toast.error(error.response?.data?.message || 'Failed to delete notification');
      }
    }
  };

  const handleCreate = () => {
    setSelectedNotification(null);
    setIsCreateModalOpen(true);
  };

  const handleCreateSubmit = async (data: any) => {
    try {
      setLoading(true);
      await adminNotificationService.createNotification(data);
      toast.success(t('admin.notifications.createdSuccess') || 'Notification created successfully');
      setIsCreateModalOpen(false);
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to create notification');
    } finally {
      setLoading(false);
    }
  };

  const handleEditSubmit = async (data: any) => {
    if (!selectedNotification) return;
    try {
      setLoading(true);
      await adminNotificationService.updateNotification(selectedNotification.id, data);
      toast.success(t('admin.notifications.updatedSuccess') || 'Notification updated successfully');
      setIsEditModalOpen(false);
      setSelectedNotification(null);
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to update notification');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[var(--color-surface-bg)]">
      <div className="container mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center gap-3 mb-2">
            <BellIcon className="w-8 h-8 text-[var(--color-primary-bg)]" />
            <div>
              <h1 className="text-3xl font-bold text-[var(--color-surface-text)]">
                {t('admin.notifications.title') || 'Admin Notifications'}
              </h1>
              <p className="text-[var(--color-secondary-text)]">
                {t('admin.notifications.subtitle') || 'Manage system notifications for users'}
              </p>
            </div>
          </div>
        </div>

        {/* Notifications Table */}
        <Card variant="outline" padding="md">
          <CardContent>
            <FlexibleCRUDTable
              entity={adminNotificationEntity}
              service={notificationService}
              onView={handleView}
              onEdit={handleEdit}
              onDelete={handleDelete}
              onCreate={handleCreate}
              searchPlaceholder={t('admin.notifications.searchPlaceholder') || 'Search notifications...'}
              showPagination={true}
              showSearch={true}
            />
          </CardContent>
        </Card>

        {/* View Modal */}
        {isViewModalOpen && selectedNotification && (
          <ViewModal
            isOpen={isViewModalOpen}
            onClose={() => {
              setIsViewModalOpen(false);
              setSelectedNotification(null);
            }}
            title={t('admin.notifications.viewNotification') || 'View Notification'}
            data={selectedNotification}
            fields={adminNotificationEntity.fields.filter(f => !f.hidden).map(f => ({
              key: f.key || f.name || '',
              label: f.label,
              type: f.type
            }))}
          />
        )}

        {/* Edit Modal */}
        {isEditModalOpen && selectedNotification && (
          <EditModal
            isOpen={isEditModalOpen}
            onClose={() => {
              setIsEditModalOpen(false);
              setSelectedNotification(null);
            }}
            onCancel={() => {
              setIsEditModalOpen(false);
              setSelectedNotification(null);
            }}
            title={t('admin.notifications.editNotification') || 'Edit Notification'}
            entity={adminNotificationEntity}
            initialData={selectedNotification}
            onSubmit={handleEditSubmit}
            loading={loading}
          />
        )}

        {/* Create Modal */}
        {isCreateModalOpen && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <Card variant="elevated" padding="lg" className="max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <CardHeader>
                <CardTitle>{t('admin.notifications.createNotification') || 'Create Notification'}</CardTitle>
              </CardHeader>
              <CardContent>
                <DynamicForm
                  entity={adminNotificationEntity}
                  onSubmit={handleCreateSubmit}
                  onCancel={() => setIsCreateModalOpen(false)}
                  loading={loading}
                  submitLabel={t('common.create') || 'Create'}
                />
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
};


